// Every /api response is {data, meta}; errors are {error:{code,message,details}, meta}.
// parseEnvelope unwraps `data`, validates it with the caller's domain zod
// schema, and surfaces meta.freshness to the UI. Scoped to /api ONLY:
// /health/ready lives outside /api, is not proxied through the API client,
// and must never be sent through this parser -- see apiOrigin.test.ts /
// envelope.test.ts for the assertion that no envelope-parsed payload carries
// an access_token key.
import { z } from 'zod';

export const freshnessSchema = z
  .object({
    computed_at: z.string().optional(),
    stale_after: z.string().optional(),
    status: z.enum(['ok', 'derived', 'stale', 'insufficient']).optional(),
  })
  .partial();

export const metaSchema = z
  .object({
    freshness: freshnessSchema.optional(),
    next_cursor: z.string().nullable().optional(),
  })
  .passthrough();

export function envelopeSchema<T extends z.ZodTypeAny>(dataSchema: T) {
  return z.object({
    data: dataSchema,
    meta: metaSchema.optional(),
  });
}

export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
  meta: metaSchema.optional(),
});

export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

/** No parsed envelope may ever carry a bare `access_token` key at the top of `data`. */
function assertNoAccessToken(data: unknown): void {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if ('access_token' in (data as Record<string, unknown>)) {
      throw new Error('envelope data must never carry an access_token key');
    }
  }
}

export function parseEnvelope<T extends z.ZodTypeAny>(
  dataSchema: T,
  payload: unknown,
): { data: z.infer<T>; meta: z.infer<typeof metaSchema> | undefined } {
  const parsed = envelopeSchema(dataSchema).parse(payload) as {
    data: z.infer<T>;
    meta: z.infer<typeof metaSchema> | undefined;
  };
  assertNoAccessToken(parsed.data);
  return parsed;
}

export function isErrorEnvelope(payload: unknown): payload is ErrorEnvelope {
  return errorEnvelopeSchema.safeParse(payload).success;
}
